import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useCredits } from '@/hooks/useCredits';
import type { CreditBalance, CreditActionType } from '@/types/billing';

interface CreditContextValue {
  balance: CreditBalance | null;
  loading: boolean;
  error: string | null;
  totalCredits: number;
  canAfford: (action: CreditActionType) => boolean;
  canAffordAmount: (amount: number) => boolean;
  getCost: (action: CreditActionType) => number;
  refreshBalance: (bustCache?: boolean) => Promise<CreditBalance | null>;
  checkCredits: (action: CreditActionType) => Promise<boolean>;
}

const CreditContext = createContext<CreditContextValue | undefined>(undefined);

interface CreditProviderProps {
  children: ReactNode;
}

// Global event for credit refresh that works even if components unmount
const CREDIT_REFRESH_EVENT = 'credit-balance-changed';

/**
 * Trigger a global credit balance refresh
 * This works even if the calling component unmounts
 */
export function triggerCreditRefresh() {
  console.log('[CreditContext] Triggering global credit refresh event');
  window.dispatchEvent(new CustomEvent(CREDIT_REFRESH_EVENT));
}

/**
 * Provides global credit balance state that can be refreshed from anywhere
 * Use the useCreditContext() hook to access credit balance and refresh function
 */
export function CreditProvider({ children }: CreditProviderProps) {
  const credits = useCredits();

  // Listen for global refresh events
  useEffect(() => {
    const handleRefresh = async () => {
      console.log('[CreditProvider] Received refresh event, refreshing balance...');
      // Add multiple retry attempts with progressive delays
      // Backend may need time to commit transaction, so we use longer delays
      const delays = [300, 600, 900, 1200, 1500]; // Total max wait: ~4.5s

      for (let attempt = 1; attempt <= delays.length; attempt++) {
        console.log(`[CreditProvider] Refresh attempt ${attempt}/${delays.length}`);
        await new Promise(resolve => setTimeout(resolve, delays[attempt - 1]));

        // Use cache busting to ensure we get fresh data from server
        const newBalance = await credits.refreshBalance(true);
        console.log(`[CreditProvider] Attempt ${attempt} result:`, newBalance?.totalCredits);

        if (newBalance) {
          console.log('[CreditProvider] Balance refreshed successfully!');
          break;
        }
      }
    };

    window.addEventListener(CREDIT_REFRESH_EVENT, handleRefresh);
    return () => window.removeEventListener(CREDIT_REFRESH_EVENT, handleRefresh);
  }, [credits]);

  return (
    <CreditContext.Provider value={credits}>
      {children}
    </CreditContext.Provider>
  );
}

/**
 * Hook to access credit balance and refresh function
 * Must be used within CreditProvider
 */
export function useCreditContext(): CreditContextValue {
  const context = useContext(CreditContext);
  if (!context) {
    throw new Error('useCreditContext must be used within CreditProvider');
  }
  return context;
}
